import { useState, useEffect, useCallback, useRef } from 'react';
import { chatClient, ChatAction, ChatUiContext } from '../client/chatClient';
import { useRobbieUiSnapshot } from '../contexts/RobbieUiContext';

export interface ChatMessage {
  _id: string;
  role: 'user' | 'assistant' | 'system';
  /** Absent for voice turns — there is no transcript, only the clip. */
  content?: string;
  createdAt: string;
  /**
   * Present only on a user's voice turn — and `mimeType` is the field that
   * says so, since older documents can carry an empty `audio` container.
   * `url` is a local object URL for the
   * clip just recorded (own messages replay instantly, no round trip); it's
   * undefined for a turn loaded from history, since the API deliberately
   * doesn't return clip bytes on that read. `durationMs` and `peaks` — the
   * clip's waveform — are cheap enough to come back either way.
   */
  audio?: { mimeType?: string; url?: string; durationMs?: number; peaks?: number[] };
  /** The tool calls behind an assistant turn — a BOM proposal card renders from these. */
  metadata?: { actions?: ChatAction[] };
}

/**
 * Failure copy in Robbie's voice — consequence + next action, never a raw
 * transport error (axios messages like "Request failed with status code 500"
 * are system-speak and banned from the panel).
 */
const CHAT_ERROR_NOTICE = {
  history: "I couldn't pull up our conversation just now — refresh and I'll pick it back up.",
  send: "That one didn't reach me — send it again and I'm on it.",
  voice: "Your voice note didn't reach me — try recording it again, or type it and I'm on it.",
} as const;

export const useChat = (
  rfqId?: string,
  step?: number,
  tab?: string,
  onActions?: (actions: ChatAction[]) => void,
  /** The step-3 screen on view (1 context, 2 validation, 3 discovery); ignored on other steps. */
  subStep?: number
) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chips, setChips] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Distinct from `isLoading`, which starts false and only flips inside the
  // fetch effect: readers that must not act on an empty thread before the
  // real one arrives (the fresh-on-open view) need a flag that is false from
  // the first render and true only once this thread's history has settled.
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFetchingChips, setIsFetchingChips] = useState(false);
  // What the buyer is looking at and has just done, read at the moment a
  // message is sent so Robbie's answer is grounded in both.
  const getUiSnapshot = useRobbieUiSnapshot();
  // Step 3 is three screens; Robbie is told which one, so he sees its state and its tools.
  const getUiContext = useCallback(
    (override?: ChatUiContext) => {
      const snapshot = getUiSnapshot(override);
      if (step !== 3 || !subStep) return snapshot;
      return { ...(snapshot ?? {}), subStep };
    },
    [getUiSnapshot, step, subStep]
  );

  // Track previous step to avoid redundant chip fetches if step hasn't changed
  const prevStepRef = useRef(step);
  const prevTabRef = useRef(tab);

  // 1. Fetch Chat History when RFQ changes
  useEffect(() => {
    let mounted = true;
    setHistoryLoaded(false);
    const fetchHistory = async () => {
      if (!rfqId || rfqId === 'new') {
        // No thread to read — settled, and settled on empty.
        if (mounted) setHistoryLoaded(true);
        return;
      }

      setIsLoading(true);
      try {
        const history = await chatClient.getChatHistory(rfqId);
        if (mounted) {
          // Filter out system messages for the UI, keep only user and assistant
          setMessages(history.filter(m => m.role !== 'system'));
        }
      } catch (err: any) {
        if (mounted) setError(CHAT_ERROR_NOTICE.history);
      } finally {
        if (mounted) {
          setIsLoading(false);
          setHistoryLoaded(true);
        }
      }
    };

    fetchHistory();
    return () => { mounted = false; };
  }, [rfqId]);

  // 2. Fetch Dynamic Chips when Step or Tab changes
  useEffect(() => {
    let mounted = true;
    const fetchChips = async () => {
      if (!rfqId || rfqId === 'new') return;
      
      setIsFetchingChips(true);
      try {
        const newChips = await chatClient.getChatChips(rfqId, step, tab);
        if (mounted && newChips && newChips.length > 0) {
          setChips(newChips);
        }
      } catch (err) {
        console.error('Failed to load chat chips', err);
      } finally {
        if (mounted) setIsFetchingChips(false);
      }
    };

    // Only fetch if step/tab changed, or if it's the first time
    if (step !== prevStepRef.current || tab !== prevTabRef.current || chips.length === 0) {
      prevStepRef.current = step;
      prevTabRef.current = tab;
      fetchChips();
    }
    
    return () => { mounted = false; };
  }, [rfqId, step, tab]);

  // Chips refresh after a reply in case the context moved. Never awaited by a
  // send: a slow or failed chip call must not hold the conversation.
  const refreshChips = useCallback(async () => {
    if (!rfqId || rfqId === 'new') return;
    setIsFetchingChips(true);
    try {
      const newChips = await chatClient.getChatChips(rfqId, step, tab);
      if (newChips && newChips.length > 0) setChips(newChips);
    } catch {
      // Keep the chips already on screen.
    } finally {
      setIsFetchingChips(false);
    }
  }, [rfqId, step, tab]);

  // 3. Send Message. `uiContext` overrides the on-screen view for a send a
  // step makes on the buyer's behalf as its view closes (the sheet that would
  // have reported it is already gone); the recent actions ride along either way.
  const sendMessage = useCallback(async (content: string, uiContext?: ChatUiContext) => {
    if (!rfqId || rfqId === 'new' || !content.trim()) return;

    // Optimistically add user message
    const tempUserMsg: ChatMessage = {
      _id: Date.now().toString(),
      role: 'user',
      content,
      createdAt: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, tempUserMsg]);
    setIsTyping(true);
    setError(null);

    try {
      const { message: responseMsg, actions } = await chatClient.sendChatMessage(
        rfqId, content, step, tab, undefined, getUiContext(uiContext) ?? undefined
      );
      setMessages(prev => [...prev, responseMsg]);

      // The dots stop the moment the reply lands; chips refresh behind it.
      setIsTyping(false);
      if (actions.length > 0 && onActions) {
        onActions(actions);
      }
      void refreshChips();
    } catch (err: any) {
      setError(CHAT_ERROR_NOTICE.send);
      setMessages(prev => prev.filter(m => m._id !== tempUserMsg._id));
    } finally {
      setIsTyping(false);
    }
  }, [rfqId, step, tab, onActions, getUiContext, refreshChips]);

  // 4. Send Voice Message — same round trip as text, but the clip is the
  // message: no transcript, so the optimistic bubble plays back the local
  // recording instead of echoing content.
  const sendVoice = useCallback(async (
    base64: string,
    mimeType: string,
    localUrl: string,
    durationMs: number,
    peaks: number[]
  ) => {
    if (!rfqId || rfqId === 'new') return;

    const tempUserMsg: ChatMessage = {
      _id: Date.now().toString(),
      role: 'user',
      audio: { mimeType, url: localUrl, durationMs, peaks },
      createdAt: new Date().toISOString()
    };

    setMessages(prev => [...prev, tempUserMsg]);
    setIsTyping(true);
    setError(null);

    try {
      const { message: responseMsg, actions } = await chatClient.sendChatMessage(
        rfqId, undefined, step, tab, { data: base64, mimeType, durationMs, peaks }, getUiContext() ?? undefined
      );
      setMessages(prev => [...prev, responseMsg]);

      setIsTyping(false);
      if (actions.length > 0 && onActions) {
        onActions(actions);
      }
      void refreshChips();
    } catch (err: any) {
      setError(CHAT_ERROR_NOTICE.voice);
      setMessages(prev => prev.filter(m => m._id !== tempUserMsg._id));
    } finally {
      setIsTyping(false);
    }
  }, [rfqId, step, tab, onActions, getUiContext, refreshChips]);

  return {
    messages,
    chips,
    isLoading,
    historyLoaded,
    isTyping,
    error,
    isFetchingChips,
    sendMessage,
    sendVoice
  };
};

/** Everything the Robbie panel renders and sends through. */
export type ChatHandle = ReturnType<typeof useChat>;
